/**
 * Integrity Hub (The Fail-Safe)
 * Data quality monitoring, audit queue, and reimbursement tracking
 */

import React, { useEffect, useState } from 'react';
import {
  Shield, AlertTriangle, CheckCircle, Link2, FileQuestion,
  DollarSign, RefreshCw, X, Plus, Search, ExternalLink
} from 'lucide-react';
import { useAnalytics } from './AnalyticsContext';

const IntegrityHub = () => {
  const {
    integrityData,
    integrityLoading,
    integrityError,
    fetchIntegrity,
    serviceMappings,
    fetchServiceMappings,
    createServiceMapping,
    resolveAuditItem,
    linkReimbursement,
    formatCurrency
  } = useAnalytics();

  const [activeTab, setActiveTab] = useState('missing-links');
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [selectedDescription, setSelectedDescription] = useState('');
  const [newMapping, setNewMapping] = useState({
    keywordMatch: '',
    category: 'Service',
    isProfitBearing: true
  });
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchIntegrity();
    fetchServiceMappings();
  }, [fetchIntegrity, fetchServiceMappings]);

  const handleCreateMapping = async () => {
    try {
      await createServiceMapping(
        newMapping.keywordMatch,
        newMapping.category,
        newMapping.isProfitBearing
      );
      setShowMappingModal(false);
      setNewMapping({ keywordMatch: '', category: 'Service', isProfitBearing: true });
      // Refresh integrity data after adding mapping
      fetchIntegrity();
    } catch (error) {
      alert('Failed to create mapping: ' + error.message);
    }
  };

  const openMappingModal = (description) => {
    setSelectedDescription(description);
    setNewMapping({
      keywordMatch: description.split(' ')[0] || description,
      category: 'Service',
      isProfitBearing: true
    });
    setShowMappingModal(true);
  };

  if (integrityLoading && !integrityData) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-500">Loading integrity data...</p>
        </div>
      </div>
    );
  }

  if (integrityError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <AlertTriangle className="w-8 h-8 text-red-500 mx-auto mb-2" />
        <p className="text-red-700">{integrityError}</p>
        <button
          onClick={fetchIntegrity}
          className="mt-4 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
        >
          Retry
        </button>
      </div>
    );
  }

  const summary = integrityData?.summary || {};
  const missingLinks = integrityData?.missingLinks || [];
  const unmappedItems = integrityData?.unmappedItems || [];
  const reimbursements = integrityData?.reimbursements || [];

  // Filter items based on search
  const filteredMissingLinks = missingLinks.filter(item =>
    item.NAME?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.client_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredUnmappedItems = unmappedItems.filter(item =>
    item.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const tabs = [
    {
      id: 'missing-links',
      label: 'Missing Links',
      icon: Link2,
      count: missingLinks.length,
      color: 'text-orange-600 bg-orange-100'
    },
    {
      id: 'unmapped-items',
      label: 'Unmapped Items',
      icon: FileQuestion,
      count: unmappedItems.length,
      color: 'text-yellow-600 bg-yellow-100'
    },
    {
      id: 'reimbursements',
      label: 'Reimbursement Leakage',
      icon: DollarSign,
      count: reimbursements.length,
      color: 'text-red-600 bg-red-100'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Data Confidence */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <Shield className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {summary.unresolvedIssues || 0}
              </p>
              <p className="text-sm text-gray-500">Unresolved Issues</p>
            </div>
          </div>
        </div>

        {/* Missing Links */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <Link2 className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {missingLinks.length}
              </p>
              <p className="text-sm text-gray-500">Shipments Missing Invoices</p>
            </div>
          </div>
        </div>

        {/* Unmapped Items */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <FileQuestion className="w-6 h-6 text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {unmappedItems.length}
              </p>
              <p className="text-sm text-gray-500">Unmapped Descriptions</p>
            </div>
          </div>
        </div>

        {/* Reimbursement Leakage */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-red-600">
                {formatCurrency(summary.reimbursementLeakage?.total || 0)}
              </p>
              <p className="text-sm text-gray-500">Unbilled Reimbursements</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <div className="flex gap-1 p-2">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    flex items-center gap-2 px-4 py-3 rounded-lg font-medium text-sm transition-colors
                    ${activeTab === tab.id
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-600 hover:bg-gray-50'
                    }
                  `}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                  <span className={`px-2 py-0.5 text-xs rounded-full ${tab.color}`}>
                    {tab.count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Search Bar */}
        <div className="p-4 border-b border-gray-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Tab Content */}
        <div className="p-4">
          {/* Missing Links Tab */}
          {activeTab === 'missing-links' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-500 mb-4">
                These shipments are marked as completed but have no associated Square invoice items.
              </p>
              {filteredMissingLinks.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Finished</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {filteredMissingLinks.slice(0, 50).map((item) => (
                        <tr key={item.ID} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm font-medium text-blue-600">
                            #{item.ID}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {item.NAME || '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {item.client_name || 'Unknown'}
                          </td>
                          <td className="px-4 py-3">
                            <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700">
                              {item.STATUS}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {item.DATE_FINISHED ? new Date(item.DATE_FINISHED).toLocaleDateString() : '-'}
                          </td>
                          <td className="px-4 py-3">
                            <button className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1">
                              <ExternalLink className="w-3 h-3" />
                              View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-400" />
                  <p>No missing invoice links found!</p>
                </div>
              )}
            </div>
          )}

          {/* Unmapped Items Tab */}
          {activeTab === 'unmapped-items' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-gray-500">
                  These invoice descriptions don't match any service mappings.
                </p>
                <button
                  onClick={() => {
                    setSelectedDescription('');
                    setNewMapping({ keywordMatch: '', category: 'Service', isProfitBearing: true });
                    setShowMappingModal(true);
                  }}
                  className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                >
                  <Plus className="w-4 h-4" />
                  Add Mapping
                </button>
              </div>
              {filteredUnmappedItems.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Occurrences</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Value</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {filteredUnmappedItems.slice(0, 50).map((item, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-900 max-w-xs truncate">
                            {item.description}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {item.occurrenceCount}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                            {formatCurrency(item.totalValue)}
                          </td>
                          <td className="px-4 py-3">
                            <button
                              onClick={() => openMappingModal(item.description)}
                              className="px-3 py-1 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 text-sm"
                            >
                              Map Now
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-400" />
                  <p>All items are mapped!</p>
                </div>
              )}
            </div>
          )}

          {/* Reimbursements Tab */}
          {activeTab === 'reimbursements' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-500 mb-4">
                These reimbursements have been paid but not yet linked to a Square invoice.
              </p>
              {reimbursements.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date Paid</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {reimbursements.map((item) => (
                        <tr key={item.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {new Date(item.date_paid).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {item.client_name}
                          </td>
                          <td className="px-4 py-3 text-sm text-red-600 font-medium">
                            {formatCurrency(item.amount)}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">
                            {item.description || '-'}
                          </td>
                          <td className="px-4 py-3">
                            <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-700">
                              Not Billed
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <button
                              onClick={() => {
                                const invoiceId = prompt('Enter Square Invoice ID:');
                                if (invoiceId) {
                                  linkReimbursement(item.id, invoiceId);
                                }
                              }}
                              className="px-3 py-1 bg-green-100 text-green-700 rounded-md hover:bg-green-200 text-sm"
                            >
                              Link Invoice
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-400" />
                  <p>No unbilled reimbursements!</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Current Service Mappings */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Current Service Mappings ({serviceMappings.length})
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {serviceMappings.map((mapping) => (
            <div
              key={mapping.id}
              className={`
                p-3 rounded-lg border
                ${mapping.category === 'Service' ? 'border-green-200 bg-green-50' :
                  mapping.category === 'Pass-Through' ? 'border-blue-200 bg-blue-50' :
                  'border-yellow-200 bg-yellow-50'}
              `}
            >
              <div className="flex items-center justify-between">
                <span className="font-medium text-gray-900">{mapping.keyword_match}</span>
                <span className={`
                  text-xs px-2 py-0.5 rounded-full
                  ${mapping.category === 'Service' ? 'bg-green-200 text-green-800' :
                    mapping.category === 'Pass-Through' ? 'bg-blue-200 text-blue-800' :
                    'bg-yellow-200 text-yellow-800'}
                `}>
                  {mapping.category}
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {mapping.is_profit_bearing ? 'Profit-bearing' : 'Non-profit-bearing'}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Mapping Modal */}
      {showMappingModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Add Service Mapping</h3>
              <button
                onClick={() => setShowMappingModal(false)}
                className="p-1 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {selectedDescription && (
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500">Mapping for:</p>
                <p className="text-sm text-gray-900 font-medium truncate">{selectedDescription}</p>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Keyword to Match
                </label>
                <input
                  type="text"
                  value={newMapping.keywordMatch}
                  onChange={(e) => setNewMapping({ ...newMapping, keywordMatch: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Prep, Bundle, Duty"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category
                </label>
                <select
                  value={newMapping.category}
                  onChange={(e) => setNewMapping({ ...newMapping, category: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="Service">Service (Prep work)</option>
                  <option value="Pass-Through">Pass-Through (Duty, Shipping)</option>
                  <option value="Supplies">Supplies</option>
                </select>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="profitBearing"
                  checked={newMapping.isProfitBearing}
                  onChange={(e) => setNewMapping({ ...newMapping, isProfitBearing: e.target.checked })}
                  className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                />
                <label htmlFor="profitBearing" className="text-sm text-gray-700">
                  Profit-bearing (include in profit calculations)
                </label>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowMappingModal(false)}
                className="flex-1 px-4 py-2 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateMapping}
                disabled={!newMapping.keywordMatch}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Create Mapping
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IntegrityHub;
